import { useState, useCallback, useEffect } from 'react';
import { apiCall } from '../../services/apiUtil';

export const useTeammates = (shouldLoad = true) => {
  const [teammates, setTeammates] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch user's teammates
  const fetchTeammates = useCallback(async (page = 1, limit = 50) => {
    try {
      setLoading(true);
      setError(null);

      const result = await apiCall(`/teammates?page=${page}&limit=${limit}`);
      setTeammates(result.data || []);
      return { success: true, data: result.data, pagination: result.pagination };
    } catch (err) {
      console.error('Error fetching teammates:', err);
      setError(err);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch pending teammate requests (received)
  const fetchPendingRequests = useCallback(async (page = 1, limit = 20) => {
    try {
      const result = await apiCall(`/teammates/requests/pending?page=${page}&limit=${limit}`);
      setPendingRequests(result.data || []);
      return { success: true, data: result.data, pagination: result.pagination };
    } catch (err) {
      console.error('Error fetching pending requests:', err);
      return { success: false, error: err.message };
    }
  }, []);

  // Fetch sent teammate requests
  const fetchSentRequests = useCallback(async (page = 1, limit = 20) => {
    try {
      const result = await apiCall(`/teammates/requests/sent?page=${page}&limit=${limit}`);
      setSentRequests(result.data || []);
      return { success: true, data: result.data, pagination: result.pagination };
    } catch (err) {
      console.error('Error fetching sent requests:', err);
      return { success: false, error: err.message };
    }
  }, []);

  // Get teammate status with another user
  const getTeammateStatus = useCallback(async (userId) => {
    try {
      const result = await apiCall(`/teammates/status/${userId}`);
      return { success: true, ...result };
    } catch (err) {
      console.error('Error getting teammate status:', err);
      return { success: false, error: err.message };
    }
  }, []);

  // Send teammate request
  const sendTeammateRequest = useCallback(async (receiverId, message = null) => {
    try {
      const result = await apiCall('/teammates/request', {
        method: 'POST',
        body: JSON.stringify({ receiverId, message })
      });

      // Refresh sent requests
      await fetchSentRequests();

      return { success: true, data: result };
    } catch (err) {
      console.error('Error sending teammate request:', err);
      return { success: false, error: err.message };
    }
  }, [fetchSentRequests]);

  // Accept teammate request
  const acceptTeammateRequest = useCallback(async (requestId) => {
    try {
      const result = await apiCall(`/teammates/request/${requestId}/accept`, {
        method: 'POST',
        body: JSON.stringify({})
      });

      // Update local state
      setPendingRequests(prev => prev.filter(r => r.id !== requestId));

      // Refresh teammates list
      await fetchTeammates();

      return { success: true, data: result };
    } catch (err) {
      console.error('Error accepting teammate request:', err);
      return { success: false, error: err.message };
    }
  }, [fetchTeammates]);

  // Decline teammate request
  const declineTeammateRequest = useCallback(async (requestId) => {
    try {
      await apiCall(`/teammates/request/${requestId}/decline`, {
        method: 'POST',
        body: JSON.stringify({})
      });

      // Update local state
      setPendingRequests(prev => prev.filter(r => r.id !== requestId));

      return { success: true };
    } catch (err) {
      console.error('Error declining teammate request:', err);
      return { success: false, error: err.message };
    }
  }, []);

  // Cancel sent teammate request
  const cancelTeammateRequest = useCallback(async (requestId) => {
    try {
      await apiCall(`/teammates/request/${requestId}`, { method: 'DELETE' });

      // Update local state
      setSentRequests(prev => prev.filter(r => r.id !== requestId));

      return { success: true };
    } catch (err) {
      console.error('Error canceling teammate request:', err);
      return { success: false, error: err.message };
    }
  }, []);

  // Remove a teammate
  const removeTeammate = useCallback(async (teammateUserId) => {
    try {
      await apiCall(`/teammates/${teammateUserId}`, { method: 'DELETE' });

      // Update local state
      setTeammates(prev => prev.filter(t => t.user.id !== teammateUserId));

      return { success: true };
    } catch (err) {
      console.error('Error removing teammate:', err);
      return { success: false, error: err.message };
    }
  }, []);

  // Initial load
  useEffect(() => {
    if (shouldLoad) {
      Promise.all([
        fetchTeammates(),
        fetchPendingRequests(),
        fetchSentRequests()
      ]);
    }
  }, [shouldLoad, fetchTeammates, fetchPendingRequests, fetchSentRequests]);

  return {
    teammates,
    pendingRequests,
    sentRequests,
    loading,
    error,
    fetchTeammates,
    fetchPendingRequests,
    fetchSentRequests,
    getTeammateStatus,
    sendTeammateRequest,
    acceptTeammateRequest,
    declineTeammateRequest,
    cancelTeammateRequest,
    removeTeammate,
    pendingCount: pendingRequests.length
  };
};

export default useTeammates;
